/**
 * Whether the DEVICE reports a connection, re-read on every change (#1069
 * offline state, 2026-09-25).
 *
 * The panel uses it to show what it already holds read-only, hide every write
 * and save control, and reload by itself on reconnect. The signal is
 * `networkService`: `navigator.onLine` + `online`/`offline` events on web,
 * NetInfo on native, debounced 300 ms and fired on change only.
 *
 * ⚠️ NOT `useNetworkStatus`. That hook calls `syncService.fullSync()` on every
 * offline-to-online edge, PER MOUNTED INSTANCE, so a panel that used it would
 * add one app-wide sync per open panel to every reconnect. This is the
 * lightweight precedent instead (`usePollingEnabled.ts`): state seeded from
 * `isConnected()` and one listener, with no side effect.
 *
 * ⚠️ AND NEVER INFERRED FROM AN ERROR. A failed request while online is a real
 * failure and keeps its own copy (the spec's R8); only the device's own report
 * means "offline".
 */
import { useEffect, useState } from 'react';

import { networkService } from '../../services/network';

export const useDeviceOnline = (): boolean => {
  const [online, setOnline] = useState(() => networkService.isConnected());
  useEffect(() => {
    // Re-read once on subscribe: a change that landed between the first render
    // and this effect fired no listener of ours.
    setOnline(networkService.isConnected());
    // `addListener` returns its own unsubscribe, which is the cleanup.
    return networkService.addListener(setOnline);
  }, []);
  return online;
};
